import { uuid } from '../core/utils.js'
import { ART_MODES, loadArtMode, setArtMode } from './art-mode.js'
import { TIERS, TIER_LABELS, TIER_PROFILES, setGraphicsTier } from './graphics-tier.js'
import { ACCESSIBILITY_CAPABILITIES, accessibilitySnapshot, clampUIScale, clampVolume } from './accessibility-prefs.js'

// Мост между экранами интерфейса и живым миром.
//
// Разделение труда: экраны рисует дизайнер, данные под них даёт этот модуль.
// Здесь нет разметки и стилей — только чтение состояния мира и отправка
// команд, чтобы кнопка на любом экране делала настоящее действие, а не
// показывала заглушку.
//
// Правило: ничего не выдумываем. Если данных в мире пока нет, возвращаем
// пустой список и честный флаг `available: false` — экран покажет пустое
// состояние вместо выдуманных цифр.

// --- общее ---

export function sendCommand(world, args) {
  if (!world?.network?.send || !Array.isArray(args) || !args.length) return false
  return world.network.send('command', { cmd: args[0], value: args.slice(1).join(' '), args, requestId: uuid() }) !== false
}

const distance = (a, b) => {
  if (!a || !b) return Infinity
  return Math.hypot(a.x - b.x, a.z - b.z)
}

// --- данные экранов (журнал, хозяйство, участки) ---
//
// Сервер шлёт их отдельным каналом screenData; клиент держит последнее
// состояние в network.screenData и будит подписчиков событием screen:<вид>.

export function screenData(world, kind) {
  const value = world?.network?.screenData?.[kind]
  return value === undefined ? null : value
}

// Подписка с автоматическим первым чтением. Возвращает функцию отписки —
// экран обязан её вызвать при размонтировании.
export function subscribeScreenData(world, kind, handler) {
  if (!world?.on || typeof handler !== 'function') return () => {}
  const event = `screen:${kind}`
  world.on(event, handler)
  const current = screenData(world, kind)
  if (current !== null) handler(current)
  return () => world.off?.(event, handler)
}

// Просьба к серверу прислать свежие данные (ответ придёт каналом, не в чат).
export function refreshScreens(world) {
  return sendCommand(world, ['screens'])
}

export function socialMapView(world) {
  const value = screenData(world, 'social-map')
  if (!value || typeof value !== 'object') return {
    available: false,
    social: { selfUserId: '', players: [], capabilities: {} },
    map: { destinations: [], capabilities: {} },
    lastResult: null,
  }
  return {
    available: value.available === true,
    social: {
      selfUserId: String(value.social?.selfUserId || ''),
      players: Array.isArray(value.social?.players) ? value.social.players.filter(player => player?.userId && player?.name) : [],
      capabilities: value.social?.capabilities || {},
    },
    map: {
      destinations: Array.isArray(value.map?.destinations) ? value.map.destinations.filter(item => item?.id && item?.label) : [],
      capabilities: value.map?.capabilities || {},
    },
    lastResult: value.lastResult || null,
  }
}

export function refreshSocialMap(world) {
  return sendCommand(world, ['social', 'refresh'])
}

export function teleportToDestination(world, destination) {
  if (!destination?.id || !Number.isSafeInteger(destination.version)) return false
  return sendCommand(world, ['map', 'teleport', String(destination.id), String(destination.version)])
}

export function questJournal(world) {
  const list = screenData(world, 'quests')
  return Array.isArray(list) ? list : []
}

export function economyView(world) {
  const value = screenData(world, 'economy')
  return value && typeof value === 'object' ? value : { summary: null, catalog: [], market: null, resources: [] }
}

// Чистая сборка «Рынка города» из серверных данных — вся арифметика
// экрана здесь, чтобы её можно было гонять тестами без React.
// balance — грид-баланс в целых МК (screenData 'player').
export function buildEconomyMarket(view, balance) {
  const market = view?.market && typeof view.market === 'object' ? view.market : null
  const sellPrices = market?.sellPrices && typeof market.sellPrices === 'object' ? market.sellPrices : {}
  const names = new Map((Array.isArray(view?.resources) ? view.resources : []).map(item => [item.id, item.name]))
  const stock = view?.summary?.stock && typeof view.summary.stock === 'object' ? view.summary.stock : {}
  const wallet = Number.isFinite(balance) ? balance : 0

  // Склад: все торгуемые ресурсы, даже пустые — игрок видит весь прайс.
  const goods = Object.keys(sellPrices)
    .map(id => ({
      id,
      name: names.get(id) || id,
      price: Math.max(0, Math.floor(sellPrices[id])),
      have: Math.max(0, Math.floor(stock[id] || 0)),
    }))
    .filter(item => item.price > 0)
    .sort((a, b) => b.have - a.have || a.name.localeCompare(b.name, 'ru'))

  // Каталог: производства и жильё, с честной доступностью по балансу.
  const catalog = (Array.isArray(view?.catalog) ? view.catalog : []).map(item => {
    const cost = Math.max(0, Math.floor(item.cost || 0))
    return {
      id: item.id,
      name: item.name,
      cost,
      workers: Math.max(0, Math.floor(item.workers || 0)),
      house: item.house || null,
      recipe: {
        input: Object.entries(item.input || {}).map(([id, amount]) => ({ id, name: names.get(id) || id, amount })),
        output: Object.entries(item.output || {}).map(([id, amount]) => ({ id, name: names.get(id) || id, amount })),
      },
      affordable: wallet >= cost,
      missing: wallet >= cost ? 0 : cost - wallet,
    }
  })

  const summary = view?.summary || null
  return {
    available: Boolean(market && Object.keys(sellPrices).length),
    balance: wallet,
    buyMarkup: market?.buyMarkup || null,
    goods,
    stockTotal: goods.reduce((sum, item) => sum + item.have, 0),
    catalog,
    buildings: Array.isArray(summary?.buildings)
      ? summary.buildings.map((item, index) => ({ index: index + 1, name: item.name, paused: Boolean(item.paused), idleReason: item.idleReason || null }))
      : [],
    population: {
      houses: Array.isArray(summary?.houses) ? summary.houses : [],
      residents: Array.isArray(summary?.residents) ? summary.residents : [],
      workers: summary?.workers && typeof summary.workers === 'object' ? summary.workers : null,
    },
  }
}

export function playerWallet(world) {
  const value = screenData(world, 'player')
  return value && typeof value === 'object' ? value : { balance: 0, plots: [] }
}

export function claimQuest(world, questId) {
  return sendCommand(world, ['quest', 'claim', String(questId)])
}

export function connectionView(world) {
  const state = world?.network?.connection?.snapshot?.()
  if (state) return state
  return { status: 'idle', visible: true, progress: 0, indeterminate: true, phases: [], error: null, retryAt: null }
}

export const CONNECTION = {
  retry(world) {
    if (!world?.network?.retryConnection) return false
    world.network.retryConnection()
    return true
  },
  cancel(world) {
    if (!world?.network?.cancelConnection) return false
    world.network.cancelConnection()
    return true
  },
}

export function walletPlots(world) {
  const value = screenData(world, 'wallet')
  if (!value || typeof value !== 'object') {
    return { available: false, currency: 'MK', scale: 100, balanceMinor: 0, history: [], plots: [], capabilities: {}, landPermissions: { available: false, capabilities: [], grants: [], access: [], availablePlayers: [] }, landResult: null }
  }
  return {
    available: value.available === true,
    currency: value.currency === 'MK' ? 'MK' : 'MK',
    scale: Number.isSafeInteger(value.scale) && value.scale > 0 ? value.scale : 100,
    balanceMinor: Number.isSafeInteger(value.balanceMinor) ? value.balanceMinor : 0,
    history: Array.isArray(value.history) ? value.history : [],
    plots: Array.isArray(value.plots) ? value.plots : [],
    capabilities: value.capabilities && typeof value.capabilities === 'object' ? value.capabilities : {},
    landPermissions: value.landPermissions && typeof value.landPermissions === 'object' ? {
      available: value.landPermissions.available === true,
      capabilities: Array.isArray(value.landPermissions.capabilities) ? value.landPermissions.capabilities : [],
      grants: Array.isArray(value.landPermissions.grants) ? value.landPermissions.grants : [],
      access: Array.isArray(value.landPermissions.access) ? value.landPermissions.access : [],
      availablePlayers: Array.isArray(value.landPermissions.availablePlayers) ? value.landPermissions.availablePlayers : [],
    } : { available: false, capabilities: [], grants: [], access: [], availablePlayers: [] },
    landResult: value.landResult && typeof value.landResult === 'object' ? value.landResult : null,
  }
}

export const WALLET_PLOTS = {
  refresh: world => sendCommand(world, ['wallet', 'refresh']),
  acquire: (world, plotId) => sendCommand(world, ['plots', 'acquire', String(plotId)]),
  grant: (world, plot, subjectUserId, capabilityId) => sendCommand(world, ['plots', 'grant', String(plot.id), String(plot.version), String(subjectUserId), String(capabilityId), 'confirmed']),
  revoke: (world, plot, subjectUserId, capabilityId) => sendCommand(world, ['plots', 'revoke', String(plot.id), String(plot.version), String(subjectUserId), String(capabilityId), 'confirmed']),
}

export function inventoryView(world) {
  const value = screenData(world, 'inventory')
  if (!value || typeof value !== 'object') return { available: false, capacity: 0, version: 0, items: [], equipment: { head: null, hand: null, back: null }, capabilities: {} }
  return {
    available: value.available === true,
    capacity: Number.isSafeInteger(value.capacity) ? value.capacity : 0,
    version: Number.isSafeInteger(value.version) ? value.version : 0,
    items: Array.isArray(value.items) ? value.items : [],
    equipment: value.equipment && typeof value.equipment === 'object' ? value.equipment : { head: null, hand: null, back: null },
    capabilities: value.capabilities && typeof value.capabilities === 'object' ? value.capabilities : {},
  }
}

// Сетка ячеек инвентаря: сервер отдаёт предметы со своими slotIndex,
// экран показывает ВСЕ ячейки контейнера — занятые и пустые. Чистая
// функция, чтобы раскладку можно было проверить тестом без React.
export function inventoryGrid(view) {
  const capacity = Math.max(0, Math.floor(view?.capacity || 0))
  const cells = Array.from({ length: capacity }, (_, index) => ({ index, item: null }))
  const overflow = []
  for (const item of Array.isArray(view?.items) ? view.items : []) {
    const slot = Number(item?.slotIndex)
    // Предмет вне вместимости не теряем: сервер — источник правды,
    // экран обязан показать всё, что тот прислал.
    if (Number.isInteger(slot) && slot >= 0 && slot < capacity && !cells[slot].item) cells[slot].item = item
    else overflow.push(item)
  }
  return { cells, overflow, used: cells.filter(cell => cell.item).length + overflow.length, capacity }
}

export const INVENTORY = {
  refresh: world => sendCommand(world, ['inventory', 'refresh']),
  move: (world, item, slot) => sendCommand(world, ['inventory', 'move', item.id, String(item.version), String(slot)]),
  split: (world, item, quantity, slot) => sendCommand(world, ['inventory', 'split', item.id, String(item.version), String(quantity), String(slot)]),
  stack: (world, item, target) => sendCommand(world, ['inventory', 'stack', item.id, String(item.version), target.id]),
  equip: (world, item, slot) => sendCommand(world, ['inventory', 'equip', item.id, String(item.version), String(slot)]),
  unequip: (world, item) => sendCommand(world, ['inventory', 'unequip', item.id, String(item.version)]),
  drop: (world, item) => sendCommand(world, ['inventory', 'drop', item.id, String(item.version)]),
  list: world => sendCommand(world, ['inv']),
  rez: (world, index) => sendCommand(world, ['rez', String(index)]),
  take: world => sendCommand(world, ['take']),
  wear: (world, index) => sendCommand(world, ['wear', String(index)]),
  sell: (world, price) => sendCommand(world, ['sell', String(price)]),
  buy: world => sendCommand(world, ['buy']),
}

export function marketplaceView(world) {
  const value = screenData(world, 'marketplace')
  if (!value || typeof value !== 'object') return { available: false, currency: 'MK', scale: 100, balanceMinor: 0, listings: [], mine: [], capabilities: {}, result: null }
  return {
    available: value.available === true, currency: 'MK', scale: Number.isSafeInteger(value.scale) ? value.scale : 100,
    balanceMinor: Number.isSafeInteger(value.balanceMinor) ? value.balanceMinor : 0,
    listings: Array.isArray(value.listings) ? value.listings : [], mine: Array.isArray(value.mine) ? value.mine : [],
    capabilities: value.capabilities && typeof value.capabilities === 'object' ? value.capabilities : {},
    result: value.result && typeof value.result === 'object' ? value.result : null,
  }
}

export const MARKETPLACE = {
  refresh: world => sendCommand(world, ['marketplace', 'refresh']),
  buy: (world, listing) => sendCommand(world, ['marketplace', 'buy', listing.entityId, String(listing.listingVersion)]),
  cancel: (world, listing) => sendCommand(world, ['marketplace', 'cancel', listing.entityId, String(listing.listingVersion)]),
  list: (world, object, priceMinor) => sendCommand(world, ['marketplace', 'list', object.entityId, String(object.listingVersion || 0), String(priceMinor)]),
}

export function settingsView(world) {
  const prefs = world?.prefs
  const tier = TIERS.find(id => {
    const profile = TIER_PROFILES[id]
    return profile.dpr === prefs?.dpr && profile.shadows === prefs?.shadows && profile.postprocessing === prefs?.postprocessing && profile.bloom === prefs?.bloom && profile.ao === prefs?.ao
  }) || null
  return {
    available: Boolean(prefs), tier, tiers: TIERS.map(id => ({ id, label: TIER_LABELS[id], profile: TIER_PROFILES[id] })),
    artMode: loadArtMode(), artModes: ART_MODES,
    ui: Number(prefs?.ui) || 1, actions: prefs?.actions !== false, stats: prefs?.stats === true,
    master: clampVolume(prefs?.master ?? 1), music: clampVolume(prefs?.music ?? 1), sfx: clampVolume(prefs?.sfx ?? 1), voice: clampVolume(prefs?.voice ?? 1),
    accessibility: prefs ? accessibilitySnapshot(prefs) : { ui: 1, actions: true, highContrast: false, reduceMotion: false, master: 1, capabilities: ACCESSIBILITY_CAPABILITIES.map(item => ({ ...item })) },
    graphics: prefs ? { dpr: prefs.dpr, shadows: prefs.shadows, postprocessing: prefs.postprocessing, bloom: prefs.bloom, ao: prefs.ao } : null,
  }
}

export const SETTINGS = {
  tier: (world, value) => setGraphicsTier(world, value),
  art: (_world, value) => setArtMode(value),
  volume: (world, kind, value) => {
    const methods = { master: 'setMaster', music: 'setMusic', sfx: 'setSFX', voice: 'setVoice' }
    const method = methods[kind]
    if (!method || !world?.prefs?.[method]) return false
    world.prefs[method](clampVolume(value)); return true
  },
  uiScale: (world, value) => { if (!world?.prefs?.setUI) return false; world.prefs.setUI(clampUIScale(value)); return true },
  toggle: (world, kind, value) => {
    const methods = { actions: 'setActions', stats: 'setStats', postprocessing: 'setPostprocessing', bloom: 'setBloom', ao: 'setAO', highContrast: 'setHighContrast', reduceMotion: 'setReduceMotion' }
    const method = methods[kind]
    if (!method || !world?.prefs?.[method]) return false
    world.prefs[method](value === true); return true
  },
  resetAccessibility: world => { if (!world?.prefs?.resetAccessibility) return false; world.prefs.resetAccessibility(); return true },
}

export function partyLobby(world) {
  const value = screenData(world, 'party')
  if (!value || typeof value !== 'object') {
    return { available: false, party: null, invitations: [], availablePlayers: [] }
  }
  return {
    available: value.available === true,
    party: value.party && typeof value.party === 'object' ? value.party : null,
    invitations: Array.isArray(value.invitations) ? value.invitations : [],
    availablePlayers: Array.isArray(value.availablePlayers) ? value.availablePlayers : [],
  }
}

export const PARTY = {
  create: (world, name) => sendCommand(world, ['group', 'create', String(name)]),
  invite: (world, userId) => sendCommand(world, ['group', 'invite', String(userId)]),
  accept: (world, inviteId) => sendCommand(world, ['group', 'accept', String(inviteId)]),
  decline: (world, inviteId) => sendCommand(world, ['group', 'decline', String(inviteId)]),
  leave: world => sendCommand(world, ['group', 'leave']),
  disband: world => sendCommand(world, ['group', 'disband']),
}

// --- игрок и валюта ---

export function playerCard(world) {
  const player = world?.entities?.player
  if (!player) return null
  return {
    name: player.data?.name || 'Без имени',
    avatarUrl: player.data?.avatar || null,
    isAdmin: Boolean(player.isAdmin?.()),
    isBuilder: Boolean(player.isBuilder?.() || player.isAdmin?.()),
    position: player.base?.position ? { x: player.base.position.x, y: player.base.position.y, z: player.base.position.z } : null,
  }
}

// --- люди рядом: для чата, пати и соц-экрана ---

export function peopleAround(world, { radius = 60 } = {}) {
  const me = world?.entities?.player
  const list = []
  world?.entities?.players?.forEach(player => {
    if (!player || player === me) return
    const position = player.base?.position
    const gap = distance(position, me?.base?.position)
    if (gap > radius) return
    list.push({
      id: player.data?.id,
      userId: player.data?.userId,
      name: player.data?.name || 'Без имени',
      distance: Number.isFinite(gap) ? Math.round(gap) : null,
      // ИИ-жители мира — их видно в списке отдельно
      isAI: /^(mia|зодчий)$/i.test(String(player.data?.name || '')),
      speaking: Boolean(player.speaking),
    })
  })
  return list.sort((a, b) => (a.distance ?? 1e9) - (b.distance ?? 1e9))
}

// --- голосовой канал: живые участники и их уровни ---
//
// Уровень берём у самого LiveKit (participant.audioLevel, 0..1) — это ровно
// тот сигнал, который слышат остальные, а не наш локальный анализатор.
// Если голосовая комната не поднялась, отдаём available: false, чтобы экран
// показал пустое состояние вместо выдуманных полосок.

export function voicePeers(world) {
  const room = world?.livekit?.room
  if (!room) return { available: false, connected: false, peers: [] }

  const peers = []
  const add = (participant, isMe) => {
    if (!participant) return
    const playerId = participant.identity || (isMe ? world?.network?.id : null)
    const player = playerId ? world?.entities?.getPlayer?.(playerId) : null
    peers.push({
      id: playerId,
      name: player?.data?.name || participant.name || (isMe ? 'Ты' : 'Игрок'),
      isMe: Boolean(isMe),
      isAI: /^(mia|зодчий)$/i.test(String(player?.data?.name || '')),
      speaking: Boolean(participant.isSpeaking),
      level: Number.isFinite(participant.audioLevel) ? participant.audioLevel : 0,
      micEnabled: participant.isMicrophoneEnabled !== false,
      serverMuted: Boolean(world?.livekit?.muted?.has?.(playerId)),
      quality: participant.connectionQuality || null,
    })
  }

  add(room.localParticipant, true)
  // livekit-client v2 — remoteParticipants, v1 — participants
  const remote = room.remoteParticipants || room.participants
  remote?.forEach?.(participant => add(participant, false))

  return {
    available: true,
    connected: Boolean(world?.livekit?.status?.connected),
    peers,
  }
}

// Локальная громкость конкретного участника (только у себя в ушах).
export function setPeerVolume(world, playerId, volume) {
  const room = world?.livekit?.room
  if (!room || !playerId) return false
  const remote = room.remoteParticipants || room.participants
  const participant = remote?.get?.(playerId)
  if (!participant?.setVolume) return false
  participant.setVolume(Math.max(0, Math.min(1, volume)))
  return true
}

// --- каналы чата: nearby / world / party / direct / system ---

export function chatChannels(world) {
  const messages = chatMessages(world)
  const live = screenData(world, 'chat')
  const availability = live?.channels || {}
  const nearby = peopleAround(world, { radius: 30 }).length
  const online = world?.entities?.players?.size || 0
  return [
    { id: 'nearby', name: 'РЯДОМ', count: nearby, available: availability.nearby !== false },
    { id: 'world', name: 'МИР', count: Math.max(0, online - 1), available: availability.world !== false },
    // группы в мире уже есть (/group, /g)
    { id: 'party', name: 'ГРУППА', count: messages.filter(item => item.channel === 'party').length, available: availability.party === true, hint: availability.party ? null : 'Сначала создай или прими группу' },
    // личные сообщения тоже есть (/im), но истории по собеседникам сервер не отдаёт
    { id: 'direct', name: 'ЛИЧНЫЕ', count: messages.filter(item => item.channel === 'direct').length, available: availability.direct === true, hint: availability.direct ? 'Выбери игрока в канале' : 'Нет доступных игроков' },
    { id: 'system', name: 'СИСТЕМА', count: messages.filter(item => !item.fromId).length, available: true },
  ]
}

export function chatMessages(world, { limit = 60 } = {}) {
  const live = screenData(world, 'chat')
  const local = world?.chat?.msgs || []
  const messages = Array.isArray(live?.messages) ? [...live.messages, ...local.filter(item => !item.fromId)] : local
  const unique = [...new Map(messages.map(item => [item.id, item])).values()]
  return unique.slice(-limit).map(item => ({
    id: item.id,
    from: item.from || null,
    fromId: item.fromId || null,
    body: item.body || '',
    createdAt: item.createdAt,
    system: !item.fromId,
    channel: item.channel || (!item.fromId ? 'system' : 'nearby'),
    targetUserId: item.targetUserId || null,
  }))
}

export function chatPresence(world) {
  const value = screenData(world, 'chat')
  return Array.isArray(value?.onlinePlayers) ? value.onlinePlayers.filter(player => player && player.userId && player.name) : []
}

export function sendChat(world, text, { channel = 'nearby', targetUserId = null } = {}) {
  const body = String(text || '').trim()
  if (!body) return false
  if (body.startsWith('/')) {
    world.chat.command(body)
    return true
  }
  if (!world?.entities?.player || !world?.chat?.send) return false
  return world.chat.send(body, { channel, targetUserId, requestId: uuid() }) === true
}

// --- миры и перемещение (лобби, пауза, карта) ---

// Зеркало WORLD_DESTINATIONS с сервера: id и координаты обязаны совпадать
// (проверяет scripts/screen-data.test.mjs). Здесь только подписи для экрана.
// Список сверен с миром 06.08 — телепорт больше не высаживает в чистое поле
// на месте снесённой застройки.
export const WORLDS = [
  { id: 'arrival', name: 'ОСТРОВ ПРИБЫТИЯ', hint: 'начало пути, маяк', position: [0, 1.7, 12], rotationY: Math.PI },
  { id: 'market', name: 'ТОРГОВЫЙ КРУГ', hint: 'торг и встречи', position: [200, 1.7, -14], rotationY: 0 },
  { id: 'grove', name: 'ТИХАЯ РОЩА', hint: 'костёр, тихое место', position: [0, 1.7, 186], rotationY: 0 },
  { id: 'arena', name: 'ПОЛИГОН', hint: 'башня и полоса', position: [-200, 1.7, -14], rotationY: 0 },
  { id: 'plots', name: 'КВАРТАЛ УЧАСТКОВ', hint: 'здесь строят свой дом', position: [-80, 1.7, -158], rotationY: Math.PI },
]

export function currentWorld(world) {
  const position = world?.entities?.player?.base?.position
  if (!position) return null
  let best = null
  for (const item of WORLDS) {
    const gap = Math.hypot(position.x - item.position[0], position.z - item.position[2])
    if (!best || gap < best.gap) best = { ...item, gap: Math.round(gap) }
  }
  return best
}

export function teleportTo(world, worldId) {
  const target = socialMapView(world).map.destinations.find(item => item.id === `place:${worldId}`)
  return teleportToDestination(world, target)
}

// --- персонажи (галерея из пула мира + своя ссылка) ---

export function avatarGallery(world) {
  const list = world?.network?.avatarGallery
  return Array.isArray(list) ? list : []
}

export function chooseAvatar(world, index) {
  if (!Number.isInteger(index) || index < 1) return false
  return sendCommand(world, ['avatar', 'set', String(index)])
}

export function applyAvatarUrl(world, url) {
  const value = String(url || '').trim()
  if (!value) return false
  return sendCommand(world, ['avatar', 'url', value])
}

// --- производство (экономика цепочек) ---
// Экран получает данные из чата-ответа сервера, поэтому здесь — только
// действия; сводку сервер печатает по /prod.

export const PRODUCTION = {
  summary: world => sendCommand(world, ['prod']),
  list: world => sendCommand(world, ['prod', 'list']),
  build: (world, typeId) => sendCommand(world, ['prod', 'build', String(typeId)]),
  pause: (world, index) => sendCommand(world, ['prod', 'pause', String(index)]),
  sell: (world, resource, amount) => sendCommand(world, ['prod', 'sell', String(resource), String(amount)]),
}

// --- земля и недвижимость ---

export const PROPERTY = {
  info: world => sendCommand(world, ['land']),
  list: world => sendCommand(world, ['land', 'list']),
  buy: world => sendCommand(world, ['land', 'buy']),
  sell: (world, price) => sendCommand(world, ['land', 'sell', String(price)]),
  rename: (world, name) => sendCommand(world, ['land', 'name', String(name)]),
  access: world => sendCommand(world, ['land', 'access']),
}

// --- инвентарь и вещи ---


// --- социальное ---

export const SOCIAL = {
  friends: () => false,
  addFriend: () => false,
  message: (world, userId, text) => sendChat(world, text, { channel: 'direct', targetUserId: String(userId) }),
  groupCreate: (world, name) => sendCommand(world, ['group', 'create', String(name)]),
  groupJoin: () => false,
  groupChat: (world, text) => sendChat(world, text, { channel: 'party' }),
  goToPlayer: () => false,
  who: world => refreshSocialMap(world),
}

// --- состояние мира для шапки экранов ---

// User-selectable animations come exclusively from the server catalog.
export function emoteWheel(world) {
  const value = screenData(world, 'emotes')
  if (!value || typeof value !== 'object') {
    return { available: false, catalog: [], active: null, cooldowns: {}, capabilities: {}, lastResult: null }
  }
  return {
    available: value.available === true,
    catalog: Array.isArray(value.catalog) ? value.catalog.filter(item => item && typeof item.id === 'string' && typeof item.label === 'string') : [],
    active: value.active && typeof value.active.id === 'string' ? value.active : null,
    cooldowns: value.cooldowns && typeof value.cooldowns === 'object' ? value.cooldowns : {},
    capabilities: value.capabilities && typeof value.capabilities === 'object' ? value.capabilities : {},
    lastResult: value.lastResult && typeof value.lastResult === 'object' ? value.lastResult : null,
  }
}

export const EMOTES = {
  start: (world, emoteId) => typeof emoteId === 'string' && emoteId.length > 0 && sendCommand(world, ['emote', 'start', emoteId]),
  stop: world => sendCommand(world, ['emote', 'stop']),
  refresh: world => sendCommand(world, ['screens']),
}

export function companionControl(world) {
  const value = screenData(world, 'companion')
  if (!value || typeof value !== 'object') return { available: false, identity: null, capabilities: [], memories: [], privacy: { memoryEnabled: false }, audit: [], tools: [], lastResult: null, exportData: null }
  return {
    available: value.available === true,
    identity: value.identity && typeof value.identity === 'object' ? value.identity : null,
    capabilities: Array.isArray(value.capabilities) ? value.capabilities : [],
    memories: Array.isArray(value.memories) ? value.memories : [],
    privacy: value.privacy && typeof value.privacy === 'object' ? value.privacy : { memoryEnabled: false },
    audit: Array.isArray(value.audit) ? value.audit : [],
    tools: Array.isArray(value.tools) ? value.tools : [],
    lastResult: value.lastResult && typeof value.lastResult === 'object' ? value.lastResult : null,
    exportData: value.exportData && typeof value.exportData === 'object' ? value.exportData : null,
  }
}

export const COMPANION = {
  refresh: world => sendCommand(world, ['companion', 'refresh']),
  grant: (world, capabilityId, expiresMinutes = 60) => sendCommand(world, ['companion', 'grant', String(capabilityId), String(expiresMinutes), 'confirmed']),
  revoke: (world, capabilityId) => sendCommand(world, ['companion', 'revoke', String(capabilityId)]),
  setMemoryEnabled: (world, enabled) => sendCommand(world, ['companion', 'privacy', 'memoryEnabled', String(Boolean(enabled))]),
  deleteMemory: (world, memoryId) => sendCommand(world, ['companion', 'memory-delete', String(memoryId), 'confirmed']),
  exportMemories: world => sendCommand(world, ['companion', 'memory-export']),
}

export function worldStatus(world) {
  const players = world?.entities?.players?.size || 0
  const place = currentWorld(world)
  return {
    online: Math.max(1, players),
    place: place?.name || 'МИР',
    // время суток мира: сутки идут 40 минут (world/apps/day-cycle.js)
    dayPhase: (() => {
      const DAY_MS = 40 * 60 * 1000
      const phase = (Date.now() % DAY_MS) / DAY_MS
      if (phase < 0.22) return 'ночь'
      if (phase < 0.34) return 'рассвет'
      if (phase < 0.62) return 'день'
      if (phase < 0.76) return 'закат'
      return 'ночь'
    })(),
  }
}
